/**
 * ClockInWindowCard — Owner-only Settings widget. Configures the daily
 * clock-in window during which the mandatory full-screen clock-in modal
 * is shown to non-Owner roles.
 *
 * Default: 09:30 → 12:30 user-local. Owner may change either bound.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Clock, Loader2 } from 'lucide-react';
import { clockV2API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';

export function ClockInWindowCard({ workspaceId }) {
  const { user } = useAuth();
  const { workspaceRole } = useWorkspace();
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';
  const [start, setStart] = useState('09:30');
  const [end, setEnd] = useState('12:30');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    clockV2API.getClockInWindow(workspaceId)
      .then((r) => { setStart(r.data?.start || '09:30'); setEnd(r.data?.end || '12:30'); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await clockV2API.setClockInWindow(workspaceId, start, end);
      toast.success('Clock-in window updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) return null;

  return (
    <Card className="bg-card border-border/30" data-testid="clock-in-window-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-[14px] font-display flex items-center gap-2">
          <Clock className="h-4 w-4 text-gold" /> Mandatory Clock-In Window
        </CardTitle>
        <CardDescription className="text-[11.5px]">
          The full-screen "Clock In Now" modal appears to non-Owner team members during this window each workday. They cannot interact with the CRM until they clock in or mark themselves "Not working today".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-[12px] text-muted-foreground/60 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 max-w-[320px]">
            <div>
              <Label className="text-[11.5px]">Start (24h)</Label>
              <Input
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-9 text-[13px] mt-1 bg-foreground/[0.02] border-border/30"
                data-testid="clock-in-window-start"
              />
            </div>
            <div>
              <Label className="text-[11.5px]">End (24h)</Label>
              <Input
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-9 text-[13px] mt-1 bg-foreground/[0.02] border-border/30"
                data-testid="clock-in-window-end"
              />
            </div>
          </div>
        )}
        <div>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30"
            data-testid="clock-in-window-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Clock className="h-3.5 w-3.5 mr-1.5" />}
            Save Window
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ClockInWindowCard;
